import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import TableContext from './table-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableContextProps, TableProps } from './table.types';

import './table.scss';

/**
 * The components like `Table`, `TableHead`, `TableBody`, `TableRow`, `TableCell` etc.
 * are wrappers for the HTML table elements `table`, `thead`, `tbody`, `tr`, `th`, `td` etc.
 * These components are used to build a table in a more structured way and can accept all based props.
 *
 * A table displays data in rows and columns. They can be fully customized.
 * The table displays information in a way that's easy to scan so that users can look for patterns and insights.
 * They can be embedded in primary content. They can include:
 * - A corresponding visualization
 * - Pagination
 * - Tools to query and manipulate data
 *
 * ### Usage
 *
 * ```tsx
 * import { Table, TableHeader, TableBody, TableFoot, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function CustomStaticTable() {
 *   return (
 *     <Table>
 *       <TableHeader>
 *         <TableRow>
 *           <TableCell>#</TableCell>
 *         </TableRow>
 *         <TableRow>
 *           <TableCell>Name</TableCell>
 *         </TableRow>
 *       </TableHeader>
 *       <TableBody>
 *         <TableRow>
 *           <TableCell>0</TableCell>
 *           <TableCell>Foo</TableCell>
 *         </TableRow>
 *         <TableRow>
 *           <TableCell>1</TableCell>
 *           <TableCell>Bar</TableCell>
 *         </TableRow>
 *         <TableRow>
 *           <TableCell>2</TableCell>
 *           <TableCell>Baz</TableCell>
 *         </TableRow>
 *      </TableBody>
 *      <TableFoot>
 *         <TableRow>
 *           <TableCell colSpan={2}>This table has 3 rows</TableCell>
 *         </TableRow>
 *      </TableFoot>
 *    </Table>
 *   );
 * }
 * ```
 */
const Table = forwardRef((props: TableProps, forwardedRef: ForwardedRef<HTMLTableElement> | null): ReactElement => {
  const {
    children,
    className,
    rowDensity = 'small',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const contextValue = useMemo<TableContextProps>(() => ({ rowDensity }), [rowDensity]);

  return (
    <TableContext.Provider value={contextValue}>
      <table {...rest} className={cn('rbui-table', className)} ref={forwardedRef}>
        {children}
      </table>
    </TableContext.Provider>
  );
});

Table.displayName = 'Table';

export default Table;
